/**
 * Voice Library Panel (Left Side of Voice Studio)
 *
 * Features:
 * - Search across all voices
 * - Quick filters (Featured, Has Preview, etc.)
 * - Advanced filters (Gender, Accent, Age, Category)
 * - Scrollable voice list with quick preview buttons
 * - Voice source badges (User vs Library)
 */

import { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Search,
  Star,
  Play,
  Square,
  Filter,
  Check
} from 'lucide-react';
import { type PresetVoice } from '@/lib/elevenlabs-voices';
import { cn } from '@/lib/utils';

interface VoiceLibraryPanelProps {
  voices: PresetVoice[];
  selectedVoice: PresetVoice | null;
  playingVoiceId: string | null;
  onVoiceSelect: (voice: PresetVoice) => void;
  onVoicePlay: (voiceId: string | null) => void;
  currentVoiceId?: string | null;
}

export function VoiceLibraryPanel({
  voices,
  selectedVoice,
  playingVoiceId,
  onVoiceSelect,
  onVoicePlay,
  currentVoiceId
}: VoiceLibraryPanelProps) {
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [genderFilter, setGenderFilter] = useState<string>('all');
  const [accentFilter, setAccentFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [hasPreviewOnly, setHasPreviewOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Extract unique filter options from voices
  const filterOptions = useMemo(() => {
    const genders = new Set<string>();
    const accents = new Set<string>();
    const categories = new Set<string>();

    voices.forEach(voice => {
      if (voice.gender) {
genders.add(voice.gender);
}
      if (voice.accent) {
accents.add(voice.accent);
}
      if (voice.category) {
categories.add(voice.category);
}
    });

    return {
      genders: Array.from(genders).sort(),
      accents: Array.from(accents).sort(),
      categories: Array.from(categories).sort()
    };
  }, [voices]);

  // Filtered voices
  const filteredVoices = useMemo(() => {
    const filtered = voices.filter(voice => {
      // Search filter
      const searchMatch = searchQuery.trim() === '' ||
        voice.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        voice.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        voice.labels.some(label => label.toLowerCase().includes(searchQuery.toLowerCase()));

      // Gender filter
      const genderMatch = genderFilter === 'all' || voice.gender === genderFilter;

      // Accent filter
      const accentMatch = accentFilter === 'all' || voice.accent === accentFilter;

      // Category filter
      const categoryMatch = categoryFilter === 'all' || voice.category === categoryFilter;

      // Featured filter
      const featuredMatch = !featuredOnly || voice.featured;

      // Has preview filter
      const previewMatch = !hasPreviewOnly || voice.previewUrl;

      return searchMatch && genderMatch && accentMatch && categoryMatch && featuredMatch && previewMatch;
    });

    // Sort: Currently configured voice at top, then selected, then rest
    return filtered.sort((a, b) => {
      const aIsConfigured = currentVoiceId === a.elevenLabsId;
      const bIsConfigured = currentVoiceId === b.elevenLabsId;
      const aIsSelected = selectedVoice?.id === a.id;
      const bIsSelected = selectedVoice?.id === b.id;

      if (aIsConfigured !== bIsConfigured) {
return aIsConfigured ? -1 : 1;
}
      if (aIsSelected !== bIsSelected) {
return aIsSelected ? -1 : 1;
}
      return 0; // Keep original order for rest
    });
  }, [voices, searchQuery, genderFilter, accentFilter, categoryFilter, featuredOnly, hasPreviewOnly, currentVoiceId, selectedVoice]);

  // Quick play/stop toggle
  const handleQuickPlay = (voice: PresetVoice, e: React.MouseEvent) => {
    e.stopPropagation();
    if (playingVoiceId === voice.id) {
      onVoicePlay(null); // Stop
    } else {
      onVoicePlay(voice.id); // Play
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setGenderFilter('all');
    setAccentFilter('all');
    setCategoryFilter('all');
    setFeaturedOnly(false);
    setHasPreviewOnly(false);
  };

  const hasActiveFilters = searchQuery || genderFilter !== 'all' ||
    accentFilter !== 'all' || categoryFilter !== 'all' ||
    featuredOnly || hasPreviewOnly;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b bg-background/50 backdrop-blur">
        <h4 className="font-semibold mb-3">Voice Library</h4>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search voices..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Quick Filters Toggle */}
        <div className="flex items-center gap-2 mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="flex-1"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {hasActiveFilters && (
              <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                {[
                  searchQuery && 1,
                  genderFilter !== 'all' && 1,
                  accentFilter !== 'all' && 1,
                  categoryFilter !== 'all' && 1,
                  featuredOnly && 1,
                  hasPreviewOnly && 1
                ].filter(Boolean).length}
              </Badge>
            )}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
            >
              Clear
            </Button>
          )}
        </div>

        {/* Advanced Filters (Collapsible) */}
        {showFilters && (
          <div className="mt-3 space-y-2 p-3 bg-muted/30 rounded-lg">
            {/* Gender Filter */}
            {filterOptions.genders.length > 0 && (
              <div>
                <label className="text-xs font-medium mb-1 block">Gender</label>
                <Select value={genderFilter} onValueChange={setGenderFilter}>
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Genders</SelectItem>
                    {filterOptions.genders.map(gender => (
                      <SelectItem key={gender} value={gender}>
                        {gender}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Accent Filter */}
            {filterOptions.accents.length > 0 && (
              <div>
                <label className="text-xs font-medium mb-1 block">Accent</label>
                <Select value={accentFilter} onValueChange={setAccentFilter}>
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Accents</SelectItem>
                    {filterOptions.accents.map(accent => (
                      <SelectItem key={accent} value={accent}>
                        {accent}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Category Filter */}
            {filterOptions.categories.length > 0 && (
              <div>
                <label className="text-xs font-medium mb-1 block">Category</label>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {filterOptions.categories.map(category => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Separator className="my-2" />

            {/* Toggle Filters */}
            <div className="flex gap-2">
              <Button
                variant={featuredOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setFeaturedOnly(!featuredOnly)}
                className="flex-1 h-8"
              >
                <Star className="h-3 w-3 mr-1" />
                Featured
              </Button>
              <Button
                variant={hasPreviewOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setHasPreviewOnly(!hasPreviewOnly)}
                className="flex-1 h-8"
              >
                <Play className="h-3 w-3 mr-1" />
                Preview
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="px-4 py-2 text-xs text-muted-foreground bg-muted/20">
        Showing {filteredVoices.length} of {voices.length} voices
      </div>

      {/* Voice List */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {filteredVoices.map(voice => {
            const isSelected = selectedVoice?.id === voice.id;
            const isPlaying = playingVoiceId === voice.id;
            const isCurrentlyConfigured = currentVoiceId === voice.elevenLabsId;

            return (
              <Card
                key={voice.id}
                className={cn(
                  "p-3 cursor-pointer transition-all hover:shadow-md relative",
                  isSelected && "border-primary bg-primary/5 shadow-lg",
                  isCurrentlyConfigured && "border-green-600 bg-green-50 shadow-xl ring-2 ring-green-500/50",
                  isPlaying && "animate-pulse"
                )}
                onClick={() => onVoiceSelect(voice)}
              >
                {/* Active Voice Badge */}
                {isCurrentlyConfigured && (
                  <div className="absolute -top-2 -right-2 bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-lg">
                    <Star className="h-3 w-3 fill-yellow-300 text-yellow-300" />
                    ACTIVE
                  </div>
                )}

                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    {/* Voice Name */}
                    <div className="flex items-center gap-1.5 mb-1">
                      {voice.featured && !isCurrentlyConfigured && (
                        <Star className="h-3 w-3 text-yellow-500 shrink-0" />
                      )}
                      {isCurrentlyConfigured && (
                        <Star className="h-4 w-4 text-green-600 fill-green-600 shrink-0" />
                      )}
                      <h5 className={cn(
                        "font-medium text-sm truncate",
                        isCurrentlyConfigured && "text-green-900 font-semibold"
                      )}>
                        {voice.name}
                      </h5>
                      {isCurrentlyConfigured && (
                        <Check className="h-4 w-4 text-green-600 shrink-0" />
                      )}
                    </div>

                    {/* Badges */}
                    <div className="flex gap-1 mb-2 flex-wrap">
                      <Badge
                        variant={voice.source === 'user' ? 'default' : 'secondary'}
                        className="text-xs h-5"
                      >
                        {voice.source === 'user' ? 'Your Voice' : 'Library'}
                      </Badge>
                      {voice.category && (
                        <Badge variant="outline" className="text-xs h-5">
                          {voice.category}
                        </Badge>
                      )}
                    </div>

                    {/* Metadata */}
                    {(voice.gender || voice.accent || voice.age) && (
                      <div className="flex gap-2 text-xs text-muted-foreground mb-1">
                        {voice.gender && <span>👤 {voice.gender}</span>}
                        {voice.accent && <span>🌍 {voice.accent}</span>}
                        {voice.age && <span>🎂 {voice.age}</span>}
                      </div>
                    )}

                    {/* Description */}
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {voice.description}
                    </p>
                  </div>

                  {/* Play Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={(e) => handleQuickPlay(voice, e)}
                    aria-label={isPlaying ? `Stop preview for ${voice.name}` : `Play preview for ${voice.name}`}
                  >
                    {isPlaying ? (
                      <Square className="h-4 w-4" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </Card>
            );
          })}

          {filteredVoices.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No voices found matching your filters</p>
              {hasActiveFilters && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={clearFilters}
                  className="mt-2"
                >
                  Clear filters
                </Button>
              )}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
